import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, X, Users, Loader2, Mail, CheckCheck, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import {
    listNotifications,
    getUnreadNotificationCount,
    markNotificationRead,
    markAllNotificationsRead,
    acceptInvite,
    declineInvite,
} from "../lib/api";
import InviteResponseModal from "./InviteResponseModal";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "./ui/popover";
import { useFeatureFlags } from "../lib/feature_flags";

/**
 * Bell icon + unread badge + dropdown panel + invite accept/decline flow.
 *
 * The parent (TopNav profile dropdown) drops this in-place. It polls
 * `/api/notifications/unread-count` every 30s so the badge stays fresh even
 * while the panel is closed.
 */
export default function NotificationBell({ onWorkgroupChanged }) {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [unread, setUnread] = useState(0);
    const [activeInvite, setActiveInvite] = useState(null);
    // iter-111: when the workgroup-chat flag is OFF, drop every
    // `wg_chat_message` notification client-side so the bell stays
    // quiet and the panel doesn't surface chatter the user told us
    // not to show.
    const flags = useFeatureFlags();
    const showChat = flags.show_workgroup_chat !== false;
    const filterChat = (rows) =>
        showChat ? rows : rows.filter((n) => n.type !== "wg_chat_message");

    const refreshCount = useCallback(async () => {
        try {
            setUnread(await getUnreadNotificationCount());
        } catch {
            /* silent — we'll retry next poll */
        }
    }, []);

    const loadList = useCallback(async () => {
        setLoading(true);
        try {
            const rows = filterChat(await listNotifications());
            setItems(rows);
            setUnread(rows.filter((n) => !n.read).length);
        } catch {
            toast.error("Couldn't load notifications");
        } finally {
            setLoading(false);
        }
    }, [showChat]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        refreshCount();
        const id = setInterval(refreshCount, 30_000);
        return () => clearInterval(id);
    }, [refreshCount]);

    useEffect(() => {
        if (open) loadList();
    }, [open, loadList]);

    const handleClick = async (n) => {
        if (!n.read) {
            try {
                await markNotificationRead(n.id);
                setItems((prev) =>
                    prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)),
                );
                setUnread((u) => Math.max(0, u - 1));
            } catch {
                /* ignore */
            }
        }
        if (n.type === "wg_invite_received" && n.data?.invite_id) {
            setActiveInvite({
                id: n.data.invite_id,
                workgroup_name: n.data.workgroup_name,
                invited_by_name: n.data.invited_by_name,
            });
            setOpen(false);
        }
        // iter-60: chat message notification → open the workgroup page
        // with the chat panel auto-selected for that workgroup.
        if (n.type === "wg_chat_message" && n.data?.workgroup_id) {
            setOpen(false);
            navigate(`/workgroup?wg=${n.data.workgroup_id}&chat=1`);
        }
        // iter-108: auto-quote-from-email notification → open the Reports
        // page filtered to that project with the Quote tab pre-selected
        // so the user lands directly on the customer-facing quote view.
        if (n.type === "email_scan_new_project" && n.data?.project_id) {
            setOpen(false);
            navigate(`/reports?project=${n.data.project_id}&view=quote`);
        }
    };

    const handleAccept = async (inviteId) => {
        await acceptInvite(inviteId);
        toast.success("Joined workgroup");
        setActiveInvite(null);
        refreshCount();
        if (onWorkgroupChanged) onWorkgroupChanged();
    };

    const handleDecline = async (inviteId) => {
        await declineInvite(inviteId);
        toast("Invite declined");
        setActiveInvite(null);
        refreshCount();
    };

    const handleMarkAll = async () => {
        await markAllNotificationsRead();
        setItems((prev) => prev.map((n) => ({ ...n, read: true })));
        setUnread(0);
    };

    return (
        <>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        data-testid="notification-bell-button"
                        aria-label="Notifications"
                        className="relative inline-flex items-center justify-center w-9 h-9 rounded-sm bg-[#0A1128] border border-[#2A3655] text-slate-300 hover:text-white hover:border-[#00A3FF]/60 transition-colors"
                    >
                        <Bell className="w-4 h-4" />
                        {unread > 0 && (
                            <span
                                data-testid="notification-bell-badge"
                                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-[10px] font-bold text-white flex items-center justify-center border border-[#05091A] leading-none"
                            >
                                {unread > 99 ? "99+" : unread}
                            </span>
                        )}
                    </button>
                </PopoverTrigger>
                {/* Radix Popover auto-flips + shifts against the viewport, so
                    the panel can never be clipped off-screen regardless of
                    phone width or where the bell is in the top nav. */}
                <PopoverContent
                    align="end"
                    sideOffset={8}
                    collisionPadding={8}
                    className="p-0 w-[min(22rem,calc(100vw-1rem))] bg-[#0F1B3A] border-[#2A3655] rounded-sm shadow-xl overflow-hidden z-50"
                    data-testid="notification-panel"
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-[#2A3655] bg-[#0A1128]">
                        <div className="text-sm font-bold uppercase tracking-[0.14em] text-white">
                            Notifications
                        </div>
                        {items.some((n) => !n.read) && (
                            <button
                                type="button"
                                onClick={handleMarkAll}
                                data-testid="notification-mark-all-read"
                                className="text-[11px] text-[#00A3FF] hover:text-white inline-flex items-center gap-1"
                            >
                                <CheckCheck className="w-3 h-3" />
                                Mark all read
                            </button>
                        )}
                    </div>
                    <div className="max-h-[420px] overflow-y-auto">
                        {loading ? (
                            <div className="p-6 text-center text-slate-500 text-sm">
                                <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                                Loading…
                            </div>
                        ) : items.length === 0 ? (
                            <div className="p-6 text-center text-slate-500 text-sm">
                                No notifications yet.
                            </div>
                        ) : (
                            <ul className="divide-y divide-[#2A3655]/50">
                                {items.map((n) => (
                                    <li key={n.id}>
                                        <button
                                            type="button"
                                            onClick={() => handleClick(n)}
                                            data-testid={`notification-item-${n.id}`}
                                            className={`w-full text-left px-4 py-3 transition-colors ${
                                                !n.read
                                                    ? "bg-[#00A3FF]/10 hover:bg-[#00A3FF]/15"
                                                    : "hover:bg-[#1C253C]"
                                            }`}
                                        >
                                            <div className="flex items-start gap-3">
                                                <NotificationIcon type={n.type} />
                                                <div className="flex-1 min-w-0">
                                                    <div className={`text-sm font-semibold truncate ${!n.read ? "text-white" : "text-slate-300"}`}>
                                                        {n.title}
                                                    </div>
                                                    {n.body && (
                                                        <div className="text-[11px] text-slate-400 mt-0.5 line-clamp-2">
                                                            {n.body}
                                                        </div>
                                                    )}
                                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">
                                                        {relTime(n.created_at)}
                                                    </div>
                                                </div>
                                                {!n.read && (
                                                    <span className="mt-1 w-2 h-2 rounded-full bg-[#00A3FF] shrink-0" />
                                                )}
                                            </div>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </PopoverContent>
            </Popover>

            <InviteResponseModal
                invite={activeInvite}
                onClose={() => setActiveInvite(null)}
                onAccept={handleAccept}
                onDecline={handleDecline}
            />
        </>
    );
}

function NotificationIcon({ type }) {
    const common = "w-4 h-4 shrink-0 mt-0.5";
    if (type === "wg_invite_received")
        return <Users className={`${common} text-[#00A3FF]`} />;
    if (type === "wg_invite_accepted")
        return <Check className={`${common} text-emerald-400`} />;
    if (type === "wg_invite_rejected")
        return <X className={`${common} text-rose-400`} />;
    if (type === "user_not_found")
        return <Mail className={`${common} text-amber-400`} />;
    if (type === "wg_chat_message")
        return <MessageCircle className={`${common} text-sky-400`} />;
    return <Bell className={`${common} text-slate-400`} />;
}

function relTime(iso) {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    const delta = Math.max(0, Date.now() - then);
    const m = Math.floor(delta / 60_000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
}
